import React from "react";
import { useQueryClient } from "react-query";
import { useSession } from "next-auth/client";
import { useAuthContext } from "../../context/AmplifyContext";
import api from "../APIResolvers";

function useOutsideClick(elementRef: React.RefObject<any>, onOutsideClick: (event: any) => void) {
  React.useEffect(() => {
    function handleClickOutside(event: Event) {
      if (elementRef.current && !elementRef.current.contains(event.target)) {
        onOutsideClick(event);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [elementRef, onOutsideClick]);
}

function useMounted() {
  const isMounted = React.useRef(false);

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
}

function scrollToTop(value = 0, behavior?: "auto" | "smooth") {
  window.scrollTo({
    top: value,
    behavior,
  });
}

function useScrollToTop() {
  React.useEffect(() => {
    scrollToTop();
  }, []);
}

function useModalControls(initState = false) {
  const [isOpen, setIsOpen] = React.useState(initState);

  const openModal = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    openModal,
    closeModal,
    setIsOpen,
  };
}

function useAuthWindow(provider: string, storeId?: string, onClose?: () => void) {
  const queryClient = useQueryClient();
  const { authedUser } = useAuthContext();
  const [authWindow, setAuthWindow] = React.useState<Window | null>(null);
  const [onAuthed, setOnAuthed] = React.useState(() => () => {});
  const [session, isLoading] = useSession();
  const [initialToken, setInitialToken] = React.useState(session?.tokens);

  const openWindow = React.useCallback(
    async (onSuccess: () => void) => {
      if (!window) return;
      setOnAuthed(() => onSuccess);
      setInitialToken(session?.tokens);
      setAuthWindow(
        window.open(
          `/signin?provider=${provider}`,
          "Auth",
          "width=1000,height=700,left=500,top=100,status=yes,toolbar=no,menubar=no,location=no",
        ),
      );
    },
    [provider, session?.tokens],
  );

  React.useEffect(() => {
    if (authWindow) {
      const timer = setInterval(async () => {
        if (authWindow.closed) {
          clearInterval(timer);
          setAuthWindow(null);

          const isTokenChanged =
            session?.tokens && JSON.stringify(session.tokens) !== JSON.stringify(initialToken);

          if (!isTokenChanged) {
            console.warn("[AuthWindow] ❌ Token not updated");
            if (onClose) onClose();
          } else if (authedUser?.username) {
            console.log("[AuthWindow] ✅ Token updated, proceeding");

            const { accessToken, accessTokenExpires, refreshToken } = session.tokens;

            await api.token.saveToken({
              accessToken,
              accessTokenExpires,
              refreshToken,
              userID: authedUser.username,
              provider,
              storeId,
            });

            queryClient.invalidateQueries(`${provider}-token`);
            onAuthed();
          }
        }
      }, 500);

      return () => clearInterval(timer);
    }
  }, [
    authWindow,
    authedUser?.username,
    initialToken,
    onAuthed,
    provider,
    queryClient,
    session,
    isLoading,
    storeId,
    onClose,
  ]);

  return { openWindow };
}

export { useOutsideClick, useScrollToTop, useMounted, useModalControls, useAuthWindow };
